/**
 * src/components/capture/UploadFilesPanel.jsx -- v24.0 Knowledge Vault, slice E.
 *
 * The WikiLLM smart-capture entry panel: drop any file (or type a quick note) ->
 * captureAndFile sequences classify -> the always-shown ScopeConfirmSheet ->
 * store-by-size -> file into the right subsystem. This component owns ONLY the UI
 * (drop zone + note field + per-item queue) and the promise bridge that turns the
 * imperative confirmScope(prefill) dep into the declarative <ScopeConfirmSheet>.
 *
 * The view scope is {} here (global Documents tab): the user always picks the
 * anchor on the sheet. When mounted inside a project/deal/vessel, pass `context`
 * and the sheet pre-fills it.
 *
 * Tokens only; 44px touch targets; mirrors DocumentsTab's UploadZone idiom. EN+ES.
 */

import { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { Upload, CheckCircle2, XCircle, Loader2, Copy } from 'lucide-react';
import { useAppDataContext } from '../../lib/AppDataContext';
import { useT } from '../../lib/i18n/I18nContext';
import { captureAndFile } from '../../lib/capture/captureAndFile';
import { buildCaptureDeps } from '../../lib/capture/buildCaptureDeps';
import { logAppError } from '../../lib/errorReporter';
import useCaptureRouter from '../../lib/capture/useCaptureRouter';
import { isAcceptableAudioFile } from '../../lib/voice/isAcceptableAudioFile';
import ScopeConfirmSheet from './ScopeConfirmSheet';

// Known error codes -> i18n key for friendly copy. Anything else shows raw.
const ERROR_KEY = {
  unsupported_mime: 'vaultCapture.errUnsupportedMime',
  audio_needs_scope: 'vaultCapture.errAudioNeedsScope',
  empty_note: 'vaultCapture.errEmptyNote',
  invoice_capture_coming_soon: 'vaultCapture.errInvoiceSoon',
  invoice_intake_failed: 'vaultCapture.errInvoiceSoon',
  too_large: 'forms.tooLarge',
  upload_failed: 'vaultCapture.errUploadFailed',
};

// Filed-status chip -> i18n key.
const STATUS_KEY = {
  pending: 'vaultCapture.statusPending',
  filed: 'vaultCapture.statusFiled',
  deduped: 'vaultCapture.statusDeduped',
  cancelled: 'vaultCapture.statusCancelled',
  error: 'vaultCapture.statusError',
};

let _seq = 0;
function nextId() {
  _seq += 1;
  return `cap-${Date.now()}-${_seq}`;
}

export default function UploadFilesPanel({ context = {}, onFiled, initialFiles = null }) {
  // Hooks at the top of the body (rules-of-hooks).
  const { t } = useT();
  const appData = useAppDataContext();
  const [queue, setQueue] = useState([]);
  const [noteText, setNoteText] = useState('');
  const [pendingConfirm, setPendingConfirm] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const projects = appData.projects || [];
  const vessels = appData.vessels || [];
  const deals = appData.crm || [];

  const friendlyError = useCallback((msg) => {
    if (!msg) return t('vaultCapture.errGeneric');
    return ERROR_KEY[msg] ? t(ERROR_KEY[msg]) : msg;
  }, [t]);

  const statusLabel = useCallback((status) => (STATUS_KEY[status] ? t(STATUS_KEY[status]) : status), [t]);

  // Promise bridge: captureAndFile calls confirmScope(prefill) and awaits; we
  // park the resolver in state and render the sheet. onConfirm/onCancel resolve.
  const confirmScope = useCallback((prefill) => {
    return new Promise((resolve) => {
      setPendingConfirm({ prefill, resolve });
    });
  }, []);

  const deps = useMemo(() => buildCaptureDeps({ confirmScope }), [confirmScope]);

  // Audio (Voice Memos, Windows Sound Recorder .m4a, ...) is not a vault document:
  // it takes the voice pipeline (direct upload -> UVTP transcription), whose
  // progress shows in the global upload tray. The vault would reject it as
  // unsupported_mime (CEO 2026-10-08).
  // TICKET-1130: never 'vessel' — no voice transport accepts a vessel scope, so a
  // project Files tab ({project_id, vessel_id}) files under the project, and a
  // vessel-only context falls to the needs-scope guard below.
  const audioContextType = context.deal_id ? 'deal' : 'project';
  const audioContextId = context.deal_id || context.project_id || null;
  const audioRouter = useCaptureRouter({
    contextType: audioContextType,
    contextId: audioContextId,
    onUpload: onFiled,
  });

  const setItem = useCallback((id, patch) => {
    setQueue((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  }, []);

  // Run one capture (file OR note) and reflect its outcome in the queue.
  const runCapture = useCallback(async (id, input) => {
    try {
      const result = await captureAndFile(input, context, deps);
      if (result.cancelled) {
        setItem(id, { status: 'cancelled' });
        return;
      }
      setItem(id, {
        status: result.deduped ? 'deduped' : 'filed',
        kind: result.kind,
      });
      // Let the host surface (Files tab) refetch so the new row appears without a
      // page reload. Optional — the global Documents tab passes nothing.
      onFiled?.(result);
    } catch (err) {
      setItem(id, { status: 'error', error: err.message });
      // DEBUG LADDER step 0: a capture failure must NEVER be a silent client
      // catch — emit to the app_error_logs spine so the failure is diagnosable.
      logAppError({
        type: 'capture_error',
        message: err?.message || 'capture_failed',
        view: 'UploadFilesPanel',
        metadata: { name: input?.name || input?.kind || null, error: err?.message || null, scope: context },
      });
    }
  }, [context, deps, setItem, onFiled]);

  const handleFiles = useCallback((files) => {
    const list = Array.from(files);
    for (const file of list.filter(isAcceptableAudioFile)) {
      const id = nextId();
      // A recording must be anchored (the voice upload requires a scope id); the
      // global Documents tab has none, so say where to drop it instead.
      if (!audioContextId) {
        setQueue((prev) => [{ id, name: file.name, status: 'error', error: 'audio_needs_scope' }, ...prev]);
        continue;
      }
      setQueue((prev) => [{ id, name: file.name, status: 'pending' }, ...prev]);
      // TICKET-1130: the router resolves per-file outcomes; only an ok result is "Filed".
      audioRouter.processFiles([file])
        .then((results) => {
          const r = Array.isArray(results) ? results.find((x) => x?.file === file) : null;
          if (r?.ok) setItem(id, { status: 'filed', kind: 'audio' });
          else setItem(id, { status: 'error', error: r?.error || 'upload_failed' });
        })
        .catch((err) => setItem(id, { status: 'error', error: err?.message })); // spine-emit-ok: useCaptureRouter emits capture_error itself
    }
    for (const file of list.filter((f) => !isAcceptableAudioFile(f))) {
      const id = nextId();
      setQueue((prev) => [{ id, name: file.name, status: 'pending' }, ...prev]);
      // Sequential awaits would block the sheet; each capture is independent and
      // pauses on its own confirm sheet. Fire them; the queue tracks each.
      runCapture(id, file);
    }
  }, [runCapture, audioRouter, audioContextId, setItem]);

  // Files handed over by another surface (the header Upload button routes docs
  // here rather than rejecting them). Runs once per distinct batch — the ref
  // guard stops a parent re-render from re-filing the same files.
  const seededRef = useRef(null);
  useEffect(() => {
    if (!initialFiles?.length || seededRef.current === initialFiles) return;
    seededRef.current = initialFiles;
    handleFiles(initialFiles);
  }, [initialFiles, handleFiles]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files?.length) handleFiles(e.dataTransfer.files);
  }, [handleFiles]);

  const handleChange = useCallback((e) => {
    if (e.target.files?.length) handleFiles(e.target.files);
    e.target.value = '';
  }, [handleFiles]);

  const handleFileNote = useCallback(() => {
    const text = noteText.trim();
    if (!text) return;
    const id = nextId();
    setQueue((prev) => [{ id, name: `${text.slice(0, 40)}${text.length > 40 ? '...' : ''}`, status: 'pending' }, ...prev]);
    setNoteText('');
    runCapture(id, { kind: 'note', text });
  }, [noteText, runCapture]);

  // Long captures finish server-side (transcription/extraction run on the worker),
  // but bytes still uploading -- or a file still awaiting its scope pick -- would be
  // lost on a full page unload. Warn before the tab closes while anything is active.
  // (In-app navigation is safe once a file is confirmed: the server keeps processing.)
  useEffect(() => {
    const active = !!pendingConfirm || queue.some((it) => it.status === 'pending');
    if (!active) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [pendingConfirm, queue]);

  const openPicker = useCallback(() => inputRef.current?.click(), []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Drop zone -- mirrors DocumentsTab UploadZone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={openPicker}
        role="button"
        tabIndex={0}
        data-testid="capture-dropzone-open"
        onKeyDown={(e) => {
          if (e.target !== e.currentTarget) return;
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openPicker(); }
        }}
        style={{
          border: `2px dashed ${dragOver ? 'var(--navy)' : 'var(--bdr)'}`,
          borderRadius: 14,
          padding: 28,
          textAlign: 'center',
          cursor: 'pointer',
          background: dragOver ? 'rgba(10,22,40,0.06)' : 'var(--glass)',
          transition: 'all 150ms ease',
        }}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          onChange={handleChange}
          data-testid="capture-file-input"
          style={{ display: 'none' }}
        />
        <Upload size={26} strokeWidth={1.5} style={{ color: 'var(--navy)', marginBottom: 8 }} />
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--t1)', marginBottom: 4 }}>
          {t('vaultCapture.dropTitle')}
        </div>
        <div style={{ fontSize: 12, color: 'var(--t3)' }}>
          {t('vaultCapture.dropSubtitle')}
        </div>
      </div>

      {/* Quick note -> filed via the note path */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
        <input
          type="text"
          value={noteText}
          onChange={(e) => setNoteText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleFileNote(); } }}
          placeholder={t('vaultCapture.notePlaceholder')}
          data-testid="capture-note-input"
          style={{
            flex: 1,
            minHeight: 44,
            padding: '8px 12px',
            borderRadius: 8,
            border: '1px solid var(--bdr)',
            background: 'var(--glass)',
            color: 'var(--t1)',
            fontSize: 13,
            outline: 'none',
          }}
        />
        <button
          type="button"
          data-testid="capture-note-submit"
          onClick={handleFileNote}
          disabled={!noteText.trim()}
          style={{
            minHeight: 44,
            padding: '8px 16px',
            border: 'none',
            borderRadius: 10,
            cursor: noteText.trim() ? 'pointer' : 'default',
            background: 'var(--navy-surface)',
            color: 'var(--glass)',
            fontSize: 13,
            fontWeight: 700,
            opacity: noteText.trim() ? 1 : 0.5,
            whiteSpace: 'nowrap',
          }}
        >
          {t('vaultCapture.fileNote')}
        </button>
      </div>

      {/* Background-processing reassurance: long uploads keep processing on the
          server even if you leave. Shown only once nothing awaits a scope pick. */}
      {!pendingConfirm && queue.some((it) => it.status === 'pending') && (
        <div
          data-testid="capture-processing-notice"
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '10px 12px', borderRadius: 10,
            background: 'var(--glass)', border: '1px solid var(--bdr)',
            fontSize: 12, color: 'var(--t1)',
          }}
        >
          <Loader2 size={14} style={{ color: 'var(--ok)', animation: 'spin 1s linear infinite', flexShrink: 0 }} />
          <span>{t('vaultCapture.processingSafe')}</span>
        </div>
      )}

      {/* Capture queue */}
      {queue.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {queue.map((item) => (
            <div
              key={item.id}
              data-testid="capture-queue-item"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '10px 12px',
                background: 'var(--glass)',
                border: '1px solid var(--bdr)',
                borderRadius: 10,
              }}
            >
              {item.status === 'pending' && <Loader2 size={16} style={{ color: 'var(--warn)', animation: 'spin 1s linear infinite', flexShrink: 0 }} />}
              {(item.status === 'filed' || item.status === 'deduped') && <CheckCircle2 size={16} style={{ color: 'var(--ok)', flexShrink: 0 }} />}
              {item.status === 'deduped' && <Copy size={13} style={{ color: 'var(--t3)', flexShrink: 0 }} />}
              {item.status === 'error' && <XCircle size={16} style={{ color: 'var(--bad)', flexShrink: 0 }} />}
              {item.status === 'cancelled' && <XCircle size={16} style={{ color: 'var(--t3)', flexShrink: 0 }} />}

              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, color: 'var(--t1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {item.name}
                </div>
                {item.status === 'error' && (
                  <div style={{ fontSize: 11, color: 'var(--bad)', marginTop: 2 }}>
                    {friendlyError(item.error)}
                  </div>
                )}
                {(item.status === 'filed' || item.status === 'deduped') && item.kind && (
                  <div style={{ fontSize: 11, color: 'var(--t3)', marginTop: 2 }}>
                    {t('vaultCapture.filedAs')} {item.kind}
                  </div>
                )}
              </div>

              <span style={{
                fontSize: 11,
                fontWeight: 600,
                flexShrink: 0,
                color: item.status === 'error'
                  ? 'var(--bad)'
                  : (item.status === 'filed' || item.status === 'deduped')
                    ? 'var(--ok)'
                    : 'var(--t3)',
              }}>
                {statusLabel(item.status)}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* The always-shown scope-confirm, resolved via the promise bridge */}
      {pendingConfirm && (
        <ScopeConfirmSheet
          prefill={pendingConfirm.prefill}
          projects={projects}
          vessels={vessels}
          deals={deals}
          onConfirm={(decision) => { pendingConfirm.resolve(decision); setPendingConfirm(null); }}
          onCancel={() => { pendingConfirm.resolve(null); setPendingConfirm(null); }}
        />
      )}
    </div>
  );
}
